import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react';
import { STORAGE_KEYS } from '@/constants';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: Theme;
  /** What is actually on screen once 'system' has been resolved. */
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Theme, held in component state rather than in a store.
 *
 * The other three portals read this from Redux, because they already have a
 * store for the session. This one has no session and therefore no store, and
 * adding Redux back for a single string would be the tail wagging the dog.
 *
 * The equivalent script in index.html runs before React mounts so there is no
 * flash on load. This handles changes after that, and keeps 'system' live: if
 * the OS flips to dark while a dentist has the page open, the page follows.
 */
function readStored(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.theme);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing, or storage disabled entirely. Not being able to
    // remember a preference is not a reason to fail to render a page.
  }
  return 'system';
}

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored);
  const [systemDark, setSystemDark] = useState(prefersDark);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEYS.theme, next);
    } catch {
      // As above: the theme still applies for this visit.
    }
  }, []);

  const dark = theme === 'dark' || (theme === 'system' && systemDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  useEffect(() => {
    // Only while following the system. A dentist who has explicitly chosen
    // light does not want it flipping at sunset.
    if (theme !== 'system') return;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setSystemDark(event.matches);
    };

    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, [theme]);

  return (
    <ThemeContext value={{ theme, resolved: dark ? 'dark' : 'light', setTheme }}>
      {children}
    </ThemeContext>
  );
}

export function useTheme(): ThemeContextValue {
  const context = use(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
